/**
 * RTE 2027 PACKAGE FREE GIFTS
 * ---------------------------------------------------------
 * Handles:
 * - Run / Walk packages
 * - Kitchen Sink
 * - Essentials
 * - Momentum
 * - Starting Line
 * - Multiple qualifying packages
 * - Multiple gift quantities
 * - Manual gift removal
 * - Package removal
 * - Duplicate gift cleanup
 * - Checkout gate: blocks a gift line from reaching
 *   checkout without its qualifying package attached
 *
 * UPDATED:
 * The gift products are now real Shopify products priced
 * at $0. No discount code / Discount Function is required
 * anymore - this script only needs to add the correct free
 * product, at the correct quantity, attached to whichever
 * qualifying package(s) are in the cart.
 *
 * FIX INCLUDED IN THIS VERSION:
 * ALL_GIFT_VARIANTS previously still pointed at the OLD gift
 * variant IDs while PACKAGE_RULES had already been updated to
 * the NEW $0 gift variant IDs. Since ALL_GIFT_VARIANTS is the
 * list the sync engine actually adds/removes/watches, the new
 * gift products would never have been added to the cart. Both
 * lists are now aligned.
 *
 * NEW: CHECKOUT GATE
 * Added a guard on the theme's checkout entry points (cart
 * page / cart drawer "Checkout" buttons and links, and any
 * form submitting to /checkout). Before the customer is
 * allowed to proceed, the script forces one more sync pass
 * and strips any gift line item that no longer has a
 * qualifying package attached (e.g. a stale drawer, a race
 * condition, or a gift line added by some other means).
 *
 * IMPORTANT LIMITATION: this can only guard entry points
 * rendered by the theme. It cannot enforce anything on the
 * actual Shopify checkout page/domain itself. For a hard,
 * server-side guarantee (e.g. against a stale/bookmarked
 * checkout link, or a cart edited directly via the API), a
 * Shopify Function / checkout validation rule is required in
 * addition to this script.
 */

(function () {
  'use strict';


  /* ========================================================
     PACKAGE RULES
  ======================================================== */

  const PACKAGE_RULES = [

    {
      name: 'KITCHEN SINK',

      triggers: [
        44081329045569, // Run
        44087138943041  // Walk
      ],

      gifts: [
        44504297537601
      ]
    },


    {
      name: 'ESSENTIALS',

      triggers: [
        44081342545985, // Run
        44087143071809  // Walk
      ],

      gifts: [
        44504388698177,
        44504397021249,
        44504400232513
      ]
    },


    {
      name: 'MOMENTUM',

      triggers: [
        44081338286145, // Run
        44081068343361  // Walk
      ],

      gifts: [
        44504397021249,
        44504400232513
      ]
    },


    {
      name: 'STARTING LINE',

      triggers: [
        44081281663041, // Run
        44081277403201  // Walk
      ],

      gifts: [
        44504400232513
      ]
    }

  ];


  /* ========================================================
     ALL GIFT VARIANTS

     IMPORTANT: this must always contain the exact same
     variant IDs used across every rule's "gifts" array above,
     with no leftovers from old/retired gift products.
  ======================================================== */

  const ALL_GIFT_VARIANTS = [

    44504297537601,

    44504388698177,

    44504397021249,

    44504400232513

  ];


  /* ========================================================
     GIFT PROPERTY
  ======================================================== */

  const GIFT_PROPERTY_KEY =
    '_rte_free_gift';


  const GIFT_PACKAGE_KEY =
    '_rte_gift_package';


  const GIFT_SOURCE_KEY =
    '_rte_gift_source';


  /* ========================================================
     CART SECTIONS
  ======================================================== */

  const BASE_SECTIONS = [

    'cart-drawer',

    'cart-icon-bubble',

    'main-cart-items',

    'main-cart-footer'

  ];


  /* ========================================================
     CHECKOUT GATE SELECTORS
  ======================================================== */

  const CHECKOUT_TRIGGER_SELECTOR =
    'a[href*="/checkout"], ' +
    'a[href="/checkout"], ' +
    'button[name="checkout"], ' +
    'input[name="checkout"], ' +
    '[data-checkout-button], ' +
    '[data-testid="Checkout-button"]';


  /* ========================================================
     STATE
  ======================================================== */

  let syncing = false;

  let syncQueued = false;

  let internalMutation = false;

  /*
   * Prevents the synchronizer from immediately adding a gift
   * that the customer just manually removed.
   */
  const manuallyRemovedGifts = new Set();


  /* ========================================================
     GET CART
  ======================================================== */

  async function getCart() {

    try {

      const response = await fetch('/cart.js', {

        credentials: 'same-origin',

        cache: 'no-store',

        headers: {
          Accept: 'application/json'
        }

      });


      if (!response.ok) {
        throw new Error('Cart request failed');
      }


      return await response.json();

    } catch (error) {

      console.warn(
        '[RTE Gifts] Unable to get cart:',
        error
      );

      return null;

    }

  }


  /* ========================================================
     FULL CART CHECK
  ======================================================== */

  function isFullCart(data) {

    return !!data &&

      Array.isArray(data.items) &&

      typeof data.total_price !== 'undefined';

  }


  /* ========================================================
     GET PACKAGE QUANTITY
  ======================================================== */

  function getPackageQuantity(cart, rule) {

    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }


    return cart.items.reduce(

      function (total, item) {

        const variantId =
          Number(item.variant_id);


        if (
          rule.triggers.includes(
            variantId
          )
        ) {

          return (
            total +
            Number(item.quantity || 0)
          );

        }


        return total;

      },

      0

    );

  }


  /* ========================================================
     REQUIRED GIFTS
  ======================================================== */

  function calculateRequiredGifts(cart) {

    const required = {};


    PACKAGE_RULES.forEach(

      function (rule) {

        const packageQuantity =
          getPackageQuantity(
            cart,
            rule
          );


        if (
          packageQuantity <= 0
        ) {
          return;
        }


        rule.gifts.forEach(

          function (giftId) {

            const id =
              Number(giftId);


            required[id] =
              (
                required[id] || 0
              ) +
              packageQuantity;

          }

        );

      }

    );


    return required;

  }



  /* ========================================================
     GET GIFT LINES
  ======================================================== */

  function getGiftLines(
    cart,
    variantId
  ) {

    return cart.items.filter(

      function (item) {

        return (

          Number(
            item.variant_id
          ) ===
          Number(variantId)

        );

      }

    );

  }


  /* ========================================================
     IS RTE GIFT
  ======================================================== */

  function isRteGift(line) {

    return !!(

      line &&

      line.properties &&

      (
        line.properties[
          GIFT_PROPERTY_KEY
        ] === 'true'

        ||

        line.properties[
          GIFT_PROPERTY_KEY
        ] === true
      )

    );

  }


  /* ========================================================
     PACKAGE NAMES FOR GIFT
  ======================================================== */

  function getGiftPackages(
    cart,
    giftVariantId
  ) {

    const names = [];


    PACKAGE_RULES.forEach(

      function (rule) {

        const qty =
          getPackageQuantity(
            cart,
            rule
          );


        if (

          qty > 0 &&

          rule.gifts.includes(
            Number(giftVariantId)
          )

        ) {

          names.push(
            rule.name
          );

        }

      }

    );


    return names;

  }


  /* ========================================================
     BUILD GIFT PROPERTIES
  ======================================================== */

  function buildGiftProperties(
    cart,
    giftVariantId
  ) {

    const packages =
      getGiftPackages(
        cart,
        giftVariantId
      );


    return {

      [GIFT_PROPERTY_KEY]:
        'true',

      [GIFT_PACKAGE_KEY]:
        packages.join(', '),

      [GIFT_SOURCE_KEY]:
        'RTE 2027 Package - Free Product'

    };

  }


  /* ========================================================
     DETERMINE IF GIFT CAN BE ADDED
  ======================================================== */

  function giftCanBeAdded(
    giftVariantId
  ) {

    return !manuallyRemovedGifts.has(
      Number(giftVariantId)
    );

  }


  /* ========================================================
     BUILD SYNC PLAN
  ======================================================== */

  function planSync(
    cart,
    requiredGifts
  ) {

    const lineUpdates = {};

    const itemsToAdd = [];


    ALL_GIFT_VARIANTS.forEach(

      function (variantId) {

        const id =
          Number(variantId);


        const requiredQty =
          Number(
            requiredGifts[id] || 0
          );


        const lines =
          getGiftLines(
            cart,
            id
          );


        const freeLines =
          lines.filter(
            isRteGift
          );


        const paidLines =
          lines.filter(

            function (line) {

              return !isRteGift(line);

            }

          );


        /* ================================================
           PACKAGE NO LONGER QUALIFIES
        ================================================= */

        if (requiredQty === 0) {

          /*
           * Remove RTE gift lines.
           */

          freeLines.forEach(

            function (line) {

              lineUpdates[
                line.key
              ] = 0;

            }

          );


          return;

        }


        /* ================================================
           NO GIFT EXISTS
        ================================================= */

        if (lines.length === 0) {

          /*
           * IMPORTANT:
           * Do NOT immediately recreate a gift that the
           * customer manually removed.
           */

          if (
            giftCanBeAdded(id)
          ) {

            itemsToAdd.push({

              id: id,

              quantity:
                requiredQty,

              properties:
                buildGiftProperties(
                  cart,
                  id
                )

            });

          }


          return;

        }


        /* ================================================
           FREE GIFT EXISTS
        ================================================= */

        if (freeLines.length > 0) {

          /*
           * Keep the first gift line.
           */

          const primary =
            freeLines[0];


          /*
           * Remove duplicate free lines.
           */

          freeLines.slice(1).forEach(

            function (line) {

              lineUpdates[
                line.key
              ] = 0;

            }

          );


          /*
           * Remove paid copy of the same gift.
           *
           * Only do this when the product is being used
           * as an RTE gift.
           */

          paidLines.forEach(

            function (line) {

              lineUpdates[
                line.key
              ] = 0;

            }

          );


          /*
           * Correct the quantity whenever it no longer
           * matches the current entitlement, in EITHER
           * direction (buying more qualifying packages
           * increases it, removing packages decreases it).
           */

          if (
            Number(primary.quantity) !==
            requiredQty
          ) {

            lineUpdates[
              primary.key
            ] = requiredQty;

          }


          return;

        }


        /* ================================================
           ONLY PAID COPY EXISTS
        ================================================= */

        if (
          freeLines.length === 0 &&
          paidLines.length > 0
        ) {

          /*
           * Do NOT automatically delete a customer's
           * normal paid purchase.
           *
           * The previous version did this and could cause
           * surprising cart behavior.
           */

          if (
            giftCanBeAdded(id)
          ) {

            itemsToAdd.push({

              id: id,

              quantity:
                requiredQty,

              properties:
                buildGiftProperties(
                  cart,
                  id
                )

            });

          }

        }

      }

    );


    return {

      lineUpdates,

      itemsToAdd

    };

  }


  /* ========================================================
     CART UPDATE
  ======================================================== */

  async function updateCart(
    updates,
    sections
  ) {

    if (
      !updates ||
      Object.keys(updates).length === 0
    ) {

      return null;

    }


    internalMutation = true;


    try {

      const response =
        await fetch(
          '/cart/update.js',
          {

            method: 'POST',

            credentials:
              'same-origin',

            headers: {

              'Content-Type':
                'application/json',

              Accept:
                'application/json'

            },

            body:
              JSON.stringify({

                updates,

                ...(sections
                  ? {
                      sections
                    }
                  : {})

              })

          }
        );


      if (!response.ok) {
        return null;
      }


      return await response.json();

    } finally {

      internalMutation = false;

    }

  }


  /* ========================================================
     CART ADD
  ======================================================== */

  async function addCartItems(
    items,
    sections
  ) {

    if (
      !items ||
      items.length === 0
    ) {

      return null;

    }


    internalMutation = true;


    try {

      const response =
        await fetch(
          '/cart/add.js',
          {

            method: 'POST',

            credentials:
              'same-origin',

            headers: {

              'Content-Type':
                'application/json',

              Accept:
                'application/json'

            },

            body:
              JSON.stringify({

                items,

                ...(sections
                  ? {
                      sections
                    }
                  : {})

              })

          }
        );


      if (!response.ok) {
        return null;
      }


      return await response.json();

    } finally {

      internalMutation = false;

    }

  }


  /* ========================================================
     SECTION IDS
  ======================================================== */

  function getSectionIds() {

    const ids =
      new Set(BASE_SECTIONS);


    document
      .querySelectorAll(
        '[data-section-id]'
      )
      .forEach(

        function (element) {

          if (
            element.dataset.sectionId
          ) {

            ids.add(
              element.dataset.sectionId
            );

          }

        }

      );


    return Array.from(ids);

  }


  /* ========================================================
     FIND SECTION
  ======================================================== */

  function findSection(
    sectionId
  ) {

    return (

      document.getElementById(
        'shopify-section-' +
        sectionId
      )

      ||

      document.querySelector(
        '[data-section-id="' +
        sectionId +
        '"]'
      )

    );

  }


  /* ========================================================
     RENDER SECTIONS
  ======================================================== */

  function renderSections(
    sections
  ) {

    if (!sections) {
      return;
    }


    Object.keys(sections)
      .forEach(

        function (sectionId) {

          const html =
            sections[sectionId];


          if (!html) {
            return;
          }


          const current =
            findSection(
              sectionId
            );


          if (!current) {
            return;
          }


          const temp =
            document.createElement(
              'div'
            );


          temp.innerHTML =
            html;


          const replacement =

            temp.querySelector(
              '#shopify-section-' +
              sectionId
            )

            ||

            temp.querySelector(
              '[data-section-id="' +
              sectionId +
              '"]'
            )

            ||

            temp.firstElementChild;


          if (replacement) {

            current.replaceWith(
              replacement
            );

          }

        }

      );

  }


  /* ========================================================
     MAIN SYNCHRONIZER
  ======================================================== */

  async function syncGift(
    seedCart
  ) {

    if (syncing) {

      syncQueued = true;

      return;

    }


    syncing = true;


    try {

      const cart =
        isFullCart(seedCart)

          ? seedCart

          : await getCart();


      if (!cart) {
        return;
      }


      const requiredGifts =
        calculateRequiredGifts(
          cart
        );


      const plan =
        planSync(
          cart,
          requiredGifts
        );


      const hasUpdates =
        Object.keys(
          plan.lineUpdates
        ).length > 0;


      const hasAdds =
        plan.itemsToAdd.length > 0;


      if (
        !hasUpdates &&
        !hasAdds
      ) {

        return;

      }


      const sectionIds =
        getSectionIds();


      let sections = null;


      /* ================================================
         REMOVE / CHANGE EXISTING LINES
      ================================================= */

      if (hasUpdates) {

        const result =
          await updateCart(

            plan.lineUpdates,

            hasAdds
              ? undefined
              : sectionIds

          );


        if (
          result &&
          result.sections
        ) {

          sections =
            result.sections;

        }

      }


      /* ================================================
         ADD ALL REQUIRED GIFTS
      ================================================= */

      if (hasAdds) {

        const result =
          await addCartItems(

            plan.itemsToAdd,

            sectionIds

          );


        if (
          result &&
          result.sections
        ) {

          sections =
            result.sections;

        }

      }


      /* ================================================
         UPDATE CART UI
      ================================================= */

      if (sections) {

        renderSections(
          sections
        );

      }


    } catch (error) {

      console.warn(
        '[RTE Gifts] Sync error:',
        error
      );

    } finally {

      syncing = false;


      if (syncQueued) {

        syncQueued = false;

        setTimeout(
          function () {

            syncGift();

          },
          100
        );

      }

    }

  }


  /* ========================================================
     CHECKOUT GATE

     Guarantees that a customer cannot reach checkout with a
     free gift line item that no longer has its qualifying
     package attached (e.g. the package was removed, its
     quantity dropped, or the gift line got out of sync some
     other way). Runs a fresh sync + verification pass, and
     force-strips anything still orphaned, before letting the
     checkout click/submit through.
  ======================================================== */

  function findOrphanGiftLines(
    cart,
    requiredGifts
  ) {

    const orphans = [];


    ALL_GIFT_VARIANTS.forEach(

      function (variantId) {

        const id =
          Number(variantId);


        const requiredQty =
          Number(
            requiredGifts[id] || 0
          );


        if (
          requiredQty > 0
        ) {

          /*
           * Package is still qualifying - this gift is
           * fine.
           */

          return;

        }


        const freeLines =
          getGiftLines(
            cart,
            id
          ).filter(
            isRteGift
          );


        freeLines.forEach(

          function (line) {

            orphans.push(
              line
            );

          }

        );

      }

    );


    return orphans;

  }


  async function enforceGiftGate() {

    /*
     * Run (and wait for) a normal sync pass first - this
     * handles the vast majority of cases the same way the
     * rest of the script does.
     */

    await syncGift();


    const cart =
      await getCart();


    if (!cart) {

      /*
       * If we can't verify the cart, fail open rather than
       * trap the customer on the cart page.
       */

      return {
        ok: true
      };

    }


    const requiredGifts =
      calculateRequiredGifts(
        cart
      );


    const orphans =
      findOrphanGiftLines(
        cart,
        requiredGifts
      );


    if (orphans.length === 0) {

      return {
        ok: true
      };

    }


    /*
     * Belt-and-braces: force-remove anything still
     * orphaned. syncGift() above should already have
     * handled this, but checkout is the last line of
     * defense, so we double check.
     */

    const lineUpdates = {};


    orphans.forEach(

      function (line) {

        lineUpdates[
          line.key
        ] = 0;

      }

    );


    const result =
      await updateCart(
        lineUpdates,
        getSectionIds()
      );


    if (
      result &&
      result.sections
    ) {

      renderSections(
        result.sections
      );

    }


    return {
      ok: false,
      removed: orphans.length
    };

  }


  function gateCheckoutTrigger(
    trigger,
    proceed
  ) {

    if (
      trigger.dataset
        .rteGiftChecked === 'true'
    ) {

      /*
       * Already verified for this click/submit cycle -
       * let it through.
       */

      return;

    }


    enforceGiftGate().then(

      function (result) {

        trigger.dataset.rteGiftChecked =
          'true';


        if (!result.ok) {

          console.warn(
            '[RTE Gifts] Removed ' +
            result.removed +
            ' gift line item(s) without a qualifying package before checkout.'
          );

        }


        proceed();


        setTimeout(

          function () {

            delete trigger
              .dataset
              .rteGiftChecked;

          },

          5000

        );

      }

    );

  }


  document.addEventListener(

    'click',

    function (event) {

      const trigger =
        event.target.closest(
          CHECKOUT_TRIGGER_SELECTOR
        );


      if (!trigger) {
        return;
      }


      if (
        trigger.dataset
          .rteGiftChecked === 'true'
      ) {

        return;

      }


      event.preventDefault();

      event.stopImmediatePropagation();


      gateCheckoutTrigger(

        trigger,

        function () {

          if (
            trigger.tagName === 'A' &&
            trigger.href
          ) {

            window.location.href =
              trigger.href;

          } else if (
            trigger.form
          ) {

            if (
              trigger.form.requestSubmit
            ) {

              trigger.form.requestSubmit(
                trigger
              );

            } else {

              trigger.form.submit();

            }

          } else {

            trigger.click();

          }

        }

      );

    },

    true

  );


  document.addEventListener(

    'submit',

    function (event) {

      const form =
        event.target;


      if (!form) {
        return;
      }


      const action =
        form.getAttribute(
          'action'
        ) || '';


      const goesToCheckout =

        action.includes(
          '/checkout'
        ) ||

        form.matches(
          'form[action*="/checkout"]'
        );


      if (!goesToCheckout) {
        return;
      }


      if (
        form.dataset
          .rteGiftChecked === 'true'
      ) {

        return;

      }


      event.preventDefault();

      event.stopImmediatePropagation();


      gateCheckoutTrigger(

        form,

        function () {

          if (
            form.requestSubmit
          ) {

            form.requestSubmit();

          } else {

            form.submit();

          }

        }

      );

    },

    true

  );


  /* ========================================================
     DETECT MANUAL CART REMOVAL
  ======================================================== */

  function inspectManualRemoval(
    url,
    data
  ) {

    if (
      internalMutation ||
      !isFullCart(data)
    ) {

      return;

    }


    /*
     * /cart/change.js and /cart/update.js return
     * the new cart.
     *
     * We can identify a gift that has disappeared
     * from the customer's cart and temporarily respect
     * that decision.
     */

    if (
      !url.includes('/cart/change') &&
      !url.includes('/cart/update')
    ) {

      return;

    }


    /*
     * Store the gift variants that are currently
     * missing from the cart.
     *
     * They can be cleared again when a qualifying
     * package quantity changes.
     */

    ALL_GIFT_VARIANTS.forEach(

      function (giftId) {

        const lines =
          getGiftLines(
            data,
            giftId
          );


        if (
          lines.length === 0
        ) {

          const required =
            calculateRequiredGifts(
              data
            );


          if (
            Number(required[giftId] || 0) > 0
          ) {

            manuallyRemovedGifts.add(
              Number(giftId)
            );

          }

        }

      }

    );

  }


  /* ========================================================
     FETCH WATCHER
  ======================================================== */

  const originalFetch =
    window.fetch;


  window.fetch =
    function (...args) {

      const url =
        typeof args[0] === 'string'

          ? args[0]

          : args[0]?.url || '';


      const isCartMutation =

        url.includes('/cart/add') ||

        url.includes('/cart/change') ||

        url.includes('/cart/update') ||

        url.includes('/cart/clear');


      const result =
        originalFetch.apply(
          this,
          args
        );


      if (
        isCartMutation &&
        !internalMutation
      ) {

        result
          .then(

            function (response) {

              return response
                .clone()
                .json()
                .catch(
                  function () {
                    return null;
                  }
                );

            }

          )
          .then(

            function (data) {

              /*
               * IMPORTANT:
               *
               * Don't immediately synchronize when the
               * customer has manually removed a gift.
               */

              inspectManualRemoval(
                url,
                data
              );


              /*
               * For package changes, synchronize.
               */

              const isPackageMutation =
                !url.includes(
                  '/cart/add'
                ) ||
                isFullCart(data);


              if (
                isPackageMutation
              ) {

                setTimeout(

                  function () {

                    syncGift(
                      isFullCart(data)
                        ? data
                        : undefined
                    );

                  },

                  150

                );

              }

            }

          )

          .catch(

            function () {

              /*
               * Don't aggressively resync after an error.
               */

            }

          );

      }


      return result;

    };


  /* ========================================================
     CLEAR MANUAL REMOVAL WHEN A PACKAGE IS CHANGED
  ======================================================== */

  document.addEventListener(
    'change',
    function (event) {

      const target =
        event.target;


      if (!target) {
        return;
      }


      if (

        target.matches(
          'input[name^="updates"], ' +
          'input[name="quantity"], ' +
          '.quantity__input, ' +
          '[data-quantity-input]'
        )

      ) {

        /*
         * A package quantity changed.
         *
         * Re-evaluate gifts from scratch.
         */

        manuallyRemovedGifts.clear();


        setTimeout(
          function () {

            syncGift();

          },
          150
        );

      }

    }
  );


  /* ========================================================
     CART FORM
  ======================================================== */

  document.addEventListener(
    'submit',
    function (event) {

      const form =
        event.target;


      if (!form) {
        return;
      }


      const action =
        form.getAttribute(
          'action'
        ) || '';


      if (

        action.includes('/cart') ||

        form.matches(
          'form[action*="/cart"]'
        )

      ) {

        /*
         * Form submission usually means a cart
         * quantity/package has changed.
         *
         * (This listens for /cart forms specifically -
         * the checkout gate above already intercepts
         * forms targeting /checkout separately.)
         */

        manuallyRemovedGifts.clear();


        setTimeout(
          function () {

            syncGift();

          },
          300
        );

      }

    },
    true
  );


  /* ========================================================
     CUSTOM CART EVENTS
  ======================================================== */

  [

    'cart:updated',

    'cart:refresh',

    'product:added',

    'cart-drawer:open'

  ].forEach(

    function (eventName) {

      document.addEventListener(

        eventName,

        function () {

          setTimeout(

            function () {

              syncGift();

            },

            150

          );

        }

      );

    }

  );


  /* ========================================================
     INITIAL LOAD
  ======================================================== */

  function initialize() {

    setTimeout(

      function () {

        /*
         * Start clean on page load.
         */

        manuallyRemovedGifts.clear();


        syncGift();

      },

      300

    );

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );

  } else {

    initialize();

  }


  /* ========================================================
     PUBLIC API
  ======================================================== */

  window.RTEFreeGift = {

    sync: function () {

      manuallyRemovedGifts.clear();

      return syncGift();

    },


    getRequiredGifts:
      async function () {

        const cart =
          await getCart();


        if (!cart) {
          return {};
        }


        return calculateRequiredGifts(
          cart
        );

      },


    getRules: function () {

      return PACKAGE_RULES;

    },


    /*
     * Exposed so you can manually re-run the checkout
     * verification/cleanup pass from the console or from
     * another script, e.g. before a custom "Checkout" CTA
     * that isn't covered by CHECKOUT_TRIGGER_SELECTOR.
     */
    enforceGiftGate:
      enforceGiftGate

  };

})();